import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserCheck, Trash2, Calculator, CheckCircle2, Banknote, CreditCard, ArrowRightLeft, TriangleAlert, ShieldCheck, MapPin, Save, Check, X, Percent } from "lucide-react";

const fmtTL = (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function VanDuty() {
  return (
    <div className="space-y-6" data-testid="van-duty">
      <DriverAssignment />
      <RegionApprovals />
      <DriverProfiles />
      <RegionCoverage />
      <DiscountUsage />
      <Reconciliation />
    </div>
  );
}

function DriverAssignment() {
  const [vehicles, setVehicles] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [map, setMap] = useState({});
  const [saved, setSaved] = useState({});
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selTpl, setSelTpl] = useState("");

  const load = useCallback(() => {
    api.get("/van-sales/assignments").then((r) => {
      const m = {}, sv = {};
      (r.data?.assignments || []).forEach((a) => { m[a.vehicle_id] = a.driver_personnel_id; sv[a.vehicle_id] = a.driver_name; });
      setMap(m); setSaved(sv);
    }).catch(() => {});
    api.get("/van-sales/assignments/lock").then((r) => setLocked(!!r.data?.locked)).catch(() => {});
    api.get("/van-sales/assignment-templates").then((r) => setTemplates(r.data?.templates || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/van-sales/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    api.get("/accounting/personnel", { params: { status: "all" } }).then((r) => setPersonnel(r.data?.items || [])).catch(() => {});
    load();
  }, [load]);

  const saveAll = async () => {
    const items = Object.entries(map).filter(([, p]) => p).map(([vehicle_id, driver_personnel_id]) => ({ vehicle_id, driver_personnel_id }));
    if (!items.length) { toast.error("En az bir araca şoför seçin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/van-sales/assignments/bulk", { items });
      toast.success(`${data.saved} atama kaydedildi`);
      (data.errors || []).forEach((e) => toast.error(`${e.plate || ""}: ${e.error}`));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  const toggleLock = async () => {
    setBusy(true);
    try { await api.post("/van-sales/assignments/lock", { locked: !locked }); setLocked(!locked); toast.success(!locked ? "Gün kilitlendi — atamalar donduruldu" : "Kilit açıldı"); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy(false); }
  };
  const saveTemplate = async () => {
    const items = Object.entries(map).filter(([, p]) => p).map(([vehicle_id, driver_personnel_id]) => ({ vehicle_id, driver_personnel_id }));
    if (!items.length) { toast.error("Önce araçlara şoför seçin, sonra şablon kaydedin"); return; }
    const name = window.prompt("Şablon adı (örn. Hafta içi düzeni):");
    if (!name || !name.trim()) return;
    setBusy(true);
    try { await api.post("/van-sales/assignment-templates", { name: name.trim(), items }); toast.success("Şablon kaydedildi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  const applyTemplate = async () => {
    if (!selTpl) { toast.error("Uygulanacak şablonu seçin"); return; }
    if (locked) { toast.error("Gün kilitli — önce kilidi açın"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/van-sales/assignment-templates/${selTpl}/apply`, {});
      toast.success(`Şablon uygulandı — ${data.saved} atama`);
      (data.errors || []).forEach((e) => toast.error(`${e.plate || ""}: ${e.error}`));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Uygulanamadı"); }
    finally { setBusy(false); }
  };
  const deleteTemplate = async () => {
    if (!selTpl) return;
    setBusy(true);
    try { await api.delete(`/van-sales/assignment-templates/${selTpl}`); setSelTpl(""); toast.success("Şablon silindi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="duty-assign-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <UserCheck className="h-4 w-4 text-brand-green" /> Toplu Şoför Ataması ({today()})
          {locked && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700" data-testid="duty-locked-badge">Gün kilitli</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={toggleLock} disabled={busy} data-testid="duty-lock-toggle" className={locked ? "border-emerald-200 text-emerald-700" : "border-red-200 text-red-600"}>
            {locked ? <><Check className="mr-1 h-4 w-4" /> Kilidi Aç</> : <><ShieldCheck className="mr-1 h-4 w-4" /> Günü Kilitle</>}
          </Button>
          <Button onClick={saveAll} disabled={busy || locked} data-testid="duty-save-all">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Tümünü Kaydet</Button>
        </div>
      </div>
      <p className="mb-3 text-xs text-slate-500">Her araca sabah şoförünü seçip <b>Tümünü Kaydet</b> deyin, sonra <b>Günü Kilitle</b> ile atamaları dondurun — gün içi karışıklık olmaz.</p>
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 p-2" data-testid="duty-template-bar">
        <span className="text-xs font-medium text-slate-500">Şablon:</span>
        <select value={selTpl} onChange={(e) => setSelTpl(e.target.value)} data-testid="duty-template-select"
          className="min-w-[160px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm">
          <option value="">Kayıtlı şablon seç…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({(t.items || []).length})</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={applyTemplate} disabled={busy || !selTpl || locked} data-testid="duty-template-apply"><Check className="mr-1 h-3.5 w-3.5" /> Uygula</Button>
        <Button size="sm" variant="outline" onClick={deleteTemplate} disabled={busy || !selTpl} data-testid="duty-template-delete" className="border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
        <span className="mx-1 hidden text-slate-300 sm:inline">|</span>
        <Button size="sm" variant="outline" onClick={saveTemplate} disabled={busy} data-testid="duty-template-save"><Save className="mr-1 h-3.5 w-3.5" /> Seçimi Şablon Yap</Button>
      </div>
      <div className="space-y-1.5" data-testid="duty-grid">
        {vehicles.length === 0 && <p className="py-2 text-sm text-slate-400">Araç bulunamadı.</p>}
        {vehicles.map((v) => (
          <div key={v.id} className="flex items-center gap-3 rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid={`duty-row-${v.id}`}>
            <span className="w-24 shrink-0 font-medium text-slate-800">{v.plate}</span>
            <select value={map[v.id] || ""} disabled={locked} data-testid={`duty-select-${v.id}`}
              onChange={(e) => setMap({ ...map, [v.id]: e.target.value })}
              className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-400">
              <option value="">— şoför seç —</option>
              {personnel.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {saved[v.id] && <span className="hidden shrink-0 text-xs text-emerald-600 sm:inline">✓ {saved[v.id]}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Reconciliation() {
  const [vehicles, setVehicles] = useState([]);
  const [vid, setVid] = useState("");
  const [date, setDate] = useState(today());
  const [info, setInfo] = useState(null);
  const [counted, setCounted] = useState({ cash: "", card: "", transfer: "" });
  const [fuel, setFuel] = useState("");
  const [allowance, setAllowance] = useState("");
  const [others, setOthers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/van-sales/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {}); }, []);

  const loadInfo = useCallback(() => {
    if (!vid) { setInfo(null); return; }
    api.get("/van-sales/reconciliation", { params: { vehicle_id: vid, date } }).then((r) => {
      const d = r.data; setInfo(d); setPreview(null);
      setCounted({ cash: String(d.expected.cash || 0), card: String(d.expected.card || 0), transfer: String(d.expected.transfer || 0) });
      setFuel(String(d.defaults.fuel || 0)); setAllowance(String(d.defaults.allowance || 0));
    }).catch(() => {});
  }, [vid, date]);
  useEffect(() => { loadInfo(); }, [loadInfo]);

  const body = () => ({
    vehicle_id: vid, date,
    counted_cash: Number(counted.cash) || 0, counted_card: Number(counted.card) || 0, counted_transfer: Number(counted.transfer) || 0,
    fuel: Number(fuel) || 0, allowance: Number(allowance) || 0,
    other_expenses: others.filter((o) => o.label && Number(o.amount)).map((o) => ({ label: o.label, amount: Number(o.amount) })),
  });

  const calc = async () => {
    setBusy(true);
    try { const { data } = await api.post("/van-sales/reconciliation/finalize", { ...body(), dry_run: true }); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Hesaplanamadı"); }
    finally { setBusy(false); }
  };
  const finalize = async () => {
    setBusy(true);
    try { await api.post("/van-sales/reconciliation/finalize", body()); toast.success("Mutabakat onaylandı — prim ve komisyonlar personel carilerine işlendi"); loadInfo(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Onaylanamadı"); }
    finally { setBusy(false); }
  };

  const fin = info?.finalized;
  const R = ({ label, value, tone = "" }) => (
    <div className="flex items-center justify-between py-1 text-sm"><span className="text-slate-500">{label}</span><span className={`font-medium ${tone}`}>{value}</span></div>
  );
  const shown = preview || fin;
  const detailed = !!info?.detailed;
  const ded = info?.deductions || {};

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Calculator className="h-4 w-4 text-brand-green" /> Gün Sonu Mutabakat & Prim</div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[180px] flex-1">
          <Label className="text-xs">Araç</Label>
          <select data-testid="recon-vehicle" value={vid} onChange={(e) => setVid(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="">Araç seç</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Tarih</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="recon-date" className="mt-1" /></div>
      </div>

      {info && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Şoför</span><span className="font-medium">{info.driver_name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Fiş / Ciro</span><span className="font-medium">{info.sale_count} · {fmtTL(info.total_sales)}</span></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[["cash", "Sayılan Nakit", Banknote], ["card", "Sayılan POS", CreditCard], ["transfer", "Sayılan Havale", ArrowRightLeft]].map(([k, lbl, Icon]) => (
                <div key={k}>
                  <Label className="flex items-center gap-1 text-[11px] text-slate-500"><Icon className="h-3 w-3" /> {lbl}</Label>
                  <Input type="number" value={counted[k]} data-testid={`recon-counted-${k}`} onChange={(e) => setCounted({ ...counted, [k]: e.target.value })} className="mt-1 h-8" />
                  <span className="text-[10px] text-slate-400">bekl. {fmtTL(info.expected[k])}</span>
                </div>
              ))}
            </div>
            {(ded.fuel !== false || ded.allowance !== false) && (
              <div className="grid grid-cols-2 gap-2">
                {ded.fuel !== false && <div><Label className="text-[11px] text-slate-500">Yakıt (₺)</Label><Input type="number" value={fuel} data-testid="recon-fuel" onChange={(e) => setFuel(e.target.value)} className="mt-1 h-8" /></div>}
                {ded.allowance !== false && <div><Label className="text-[11px] text-slate-500">Yol-Yemek (₺)</Label><Input type="number" value={allowance} data-testid="recon-allowance" onChange={(e) => setAllowance(e.target.value)} className="mt-1 h-8" /></div>}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between"><Label className="text-[11px] text-slate-500">Diğer giderler</Label>
                <button onClick={() => setOthers([...others, { label: "", amount: "" }])} className="text-[11px] font-medium text-brand-green">+ ekle</button></div>
              {others.map((o, i) => (
                <div key={i} className="mt-1 flex gap-1">
                  <Input value={o.label} placeholder="Açıklama" onChange={(e) => setOthers(others.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="h-8" />
                  <Input type="number" value={o.amount} placeholder="₺" onChange={(e) => setOthers(others.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="h-8 w-24" />
                  <button onClick={() => setOthers(others.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            {!fin && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={calc} disabled={busy} data-testid="recon-calc"><Calculator className="mr-1 h-4 w-4" /> Hesapla</Button>
                <Button onClick={finalize} disabled={busy} data-testid="recon-finalize">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Mutabakatı Onayla</Button>
              </div>
            )}
            {fin && <div className="rounded-md bg-emerald-50 p-2 text-sm text-emerald-700" data-testid="recon-finalized">Bu gün için mutabakat onaylandı ({fin.finalized_at?.slice(0, 16).replace("T", " ")}).</div>}
          </div>

          <div className="rounded-lg border border-slate-200 p-3" data-testid="recon-result">
            {!shown ? <p className="py-6 text-center text-sm text-slate-400">Sonuç için "Hesapla" deyin.</p> : (
              <>
                <R label="Toplam Satış" value={fmtTL(shown.total_sales ?? info.total_sales)} />
                {detailed && (
                  <>
                    <R label="Yakıt" value={`- ${fmtTL(shown.fuel)}`} />
                    <R label="Yol-Yemek" value={`- ${fmtTL(shown.allowance)}`} />
                    <R label="POS Komisyonu" value={`- ${fmtTL(shown.pos_commission)}`} />
                    <R label="Diğer Giderler" value={`- ${fmtTL(shown.other)}`} />
                    <R label="Depo Karı" value={`- ${fmtTL(shown.warehouse_profit)}`} />
                    <R label="Depo Sorumlusu Kom." value={`- ${fmtTL(shown.warehouse_manager_commission)}`} />
                    <R label="Pazarlama Müdürü Kom." value={`- ${fmtTL(shown.sales_manager_commission)}`} />
                  </>
                )}
                <R label="Toplam Kesinti" value={`- ${fmtTL(shown.total_deductions)}`} tone="text-slate-700 font-medium" />
                <div className="my-1 border-t border-slate-100" />
                <R label="Net" value={fmtTL(shown.net)} tone="text-slate-800" />
                <R label={`Şoför Primi (%${shown.driver_prim_pct})`} value={fmtTL(shown.driver_prim)} tone="text-emerald-700 font-bold" />
                {shown.cash_shortage > 0 && (
                  <div className="mt-2 flex items-center gap-1 rounded-md bg-red-50 p-2 text-sm text-red-700" data-testid="recon-shortage">
                    <TriangleAlert className="h-4 w-4" /> Kasa açığı: {fmtTL(shown.cash_shortage)} — şoför cari borcuna eklenir
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {!info && vid && <p className="mt-4 text-sm text-slate-400">Bu araç/gün için satış bulunamadı.</p>}
    </div>
  );
}

function RegionApprovals() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/van-sales/region-requests", { params: { status: "pending" } }).then((r) => setRows(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, kind) => {
    setBusy(true);
    try { await api.post(`/van-sales/region-requests/${id}/${kind}`, {}); toast.success(kind === "approve" ? "Bölge satışı onaylandı" : "Talep reddedildi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="van-region-approvals">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><MapPin className="h-4 w-4 text-brand-green" /> Bölge Dışı Satış Onayları</div>
      {rows.length === 0 && <p className="py-2 text-sm text-slate-400">Bekleyen bölge onayı yok.</p>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid={`region-req-${r.id}`}>
            <span><span className="font-medium text-slate-800">{r.driver_name}</span> · <span className="text-amber-700">{r.region}</span> {r.customer_name && <span className="text-slate-400">· {r.customer_name}</span>} <span className="text-slate-400">· {r.date}</span></span>
            <div className="flex gap-1">
              <Button size="sm" onClick={() => act(r.id, "approve")} disabled={busy} data-testid={`region-approve-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Onayla</Button>
              <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => act(r.id, "reject")} disabled={busy} data-testid={`region-reject-${r.id}`}><X className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RegionCoverage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/van-sales/region-coverage").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="van-region-coverage">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><MapPin className="h-4 w-4 text-brand-green" /> Bölge-Şoför Kapsama Haritası</div>
      <p className="mb-3 text-xs text-slate-500">Hangi bölgede kaç şoför var? <span className="font-medium text-red-600">Boş</span> bölgeler kapsanmıyor, <span className="font-medium text-amber-600">paylaşımlı</span> bölgelerde birden çok şoför var (kural gereği bir bölge birden çok şoföre atanabilir).</p>
      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600" data-testid="rc-total">Toplam {data.total_regions} bölge</span>
        <span className="rounded-full bg-red-50 px-2.5 py-1 font-medium text-red-600" data-testid="rc-uncovered">{data.uncovered_count} boş</span>
        <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-600" data-testid="rc-shared">{data.shared_count} paylaşımlı</span>
      </div>
      <div className="grid gap-1.5 sm:grid-cols-2" data-testid="rc-grid">
        {data.rows.map((r) => (
          <div key={r.region} data-testid={`rc-row-${r.region}`}
            className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ring-1 ring-inset ${r.driver_count === 0 ? "bg-red-50 ring-red-100" : r.driver_count > 1 ? "bg-amber-50 ring-amber-100" : "bg-slate-50 ring-slate-100"}`}>
            <span className="font-medium text-slate-800">{r.region}</span>
            <span className="ml-2 truncate text-xs text-slate-500">{r.driver_count === 0 ? "— boş —" : `${r.driver_count} şoför: ${r.drivers.join(", ")}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscountUsage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/van-sales/discount-usage").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || !(data.drivers || []).length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="van-discount-usage">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Percent className="h-4 w-4 text-brand-green" /> Şoför Haftalık İskonto Hakkı</div>
      <p className="mb-3 text-xs text-slate-500">Bu hafta ({data.week_start}) kullanılan iskonto hakkı. Hakkı dolan şoför yeni iskonto uygulayamaz.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {data.drivers.map((d) => {
          const rights = d.rights || 0;
          const pct = rights ? Math.min(100, Math.round((d.used / rights) * 100)) : 0;
          const full = rights && d.used >= rights;
          const bar = full ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
          return (
            <div key={d.id} className="rounded-lg border border-slate-100 p-3" data-testid={`disc-usage-${d.id}`}>
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-800">{d.name}</span>
                <span data-testid={`disc-usage-count-${d.id}`} className={`font-semibold ${full ? "text-red-600" : "text-slate-600"}`}>{rights ? `${d.used}/${rights}` : `${d.used} · limitsiz`}</span>
              </div>
              {rights > 0 && (
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full transition-all ${bar}`} style={{ width: `${pct}%` }} data-testid={`disc-usage-bar-${d.id}`} />
                </div>
              )}
              <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
                <span>İzinli: {(d.allowed_rates || []).length ? d.allowed_rates.map((r) => `%${r}`).join(", ") : "—"}{d.custom ? " (özel)" : ""}</span>
                {rights > 0 && <span>{full ? "hak doldu" : `${d.remaining} kaldı`}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriverProfiles() {
  const [regions, setRegions] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/van-sales/drivers").then((r) => { setRegions(r.data?.regions || []); setDrivers(r.data?.drivers || []); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = (pid, region, current) => {
    const set = new Set(edit[pid] || current || []);
    if (set.has(region)) set.delete(region); else set.add(region);
    setEdit({ ...edit, [pid]: [...set] });
  };
  const save = async (d) => {
    setBusy(d.id);
    try {
      const allowed = edit[d.id] || d.allowed_regions || [];
      await api.put(`/van-sales/drivers/${d.id}/profile`, { allowed_regions: allowed, active_driver: true });
      toast.success(`${d.name}: izinli bölgeler güncellendi`); load(); setEdit((e) => { const n = { ...e }; delete n[d.id]; return n; });
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="van-driver-profiles">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><ShieldCheck className="h-4 w-4 text-brand-green" /> Şoför İzinli Bölgeleri</div>
      <p className="mb-3 text-xs text-slate-500">Seçili bölgelere şoför serbest satış yapar. Seçili olmayan bölgeye satış AMİR ONAYI ister. Hiç seçilmezse tüm bölgeler serbest.</p>
      <div className="max-h-[420px] space-y-3 overflow-auto">
        {drivers.map((d) => {
          const cur = edit[d.id] || d.allowed_regions || [];
          const dirty = !!edit[d.id];
          return (
            <div key={d.id} className="rounded-lg border border-slate-100 p-3" data-testid={`driver-profile-${d.id}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-800">{d.name} {!d.email && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">e-posta yok</span>}</span>
                <Button size="sm" variant={dirty ? "default" : "outline"} onClick={() => save(d)} disabled={busy === d.id} data-testid={`driver-save-${d.id}`}>
                  {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Kaydet</>}
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {regions.map((rg) => {
                  const on = cur.includes(rg);
                  return (
                    <button key={rg} type="button" onClick={() => toggle(d.id, rg, d.allowed_regions)} data-testid={`driver-region-${d.id}-${rg}`}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${on ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{rg}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
